import { FC, ReactElement } from 'react'
import styled, { css } from 'styled-components'
import { Box, Button, SupportMessage, Text, theme } from '@mrshmllw/smores-react'
import { Icons } from '@mrshmllw/smores-react/dist/Icon/iconsList'

import { PageWrapper } from '../../components/PageWrapper'
import BrokenMarshform from 'assets/images/marshforms/broken.svg?react'

import { useIntercom } from 'utils'

type ErrorComponentProps = {
  description: string
  handlePrimaryButtonClick?: () => void
  handleSecondaryButtonClick?: () => void
  secondaryButtonLabel?: string
  secondaryButtonIcon?: Icons | null
  primaryButtonLabel?: string
  primaryButtonIcon?: Icons | null
  title: string
  errorReference?: string
  hideIntercom?: boolean
  marshform?: ReactElement | null
}

export const ErrorComponent: FC<ErrorComponentProps> = ({
  description,
  handlePrimaryButtonClick,
  handleSecondaryButtonClick,
  secondaryButtonLabel,
  secondaryButtonIcon = 'arrow',
  primaryButtonLabel,
  primaryButtonIcon = 'renewal-line',
  title,
  errorReference,
  hideIntercom = false,
  marshform = <BrokenMarshform />,
}) => {
  const intercom = useIntercom()

  return (
    <PageWrapper>
      <ErrorPageContent>
        {marshform !== null && marshform}

        <Text mt="24px" mb="16px" typo="hero-alternate" align="center">
          {title}
        </Text>

        <Description>{description}</Description>

        <ButtonBox maxWidth="500px" $secondaryButtonLabel={secondaryButtonLabel}>
          {primaryButtonLabel && (
            <Button
              primary
              id="tryRefresh"
              icon={primaryButtonIcon === null ? undefined : primaryButtonIcon}
              handleClick={handlePrimaryButtonClick}
            >
              {primaryButtonLabel}
            </Button>
          )}

          {secondaryButtonLabel && (
            <SecondaryButton
              id={secondaryButtonLabel}
              icon={secondaryButtonIcon === null ? undefined : secondaryButtonIcon}
              handleClick={handleSecondaryButtonClick}
              secondary
            >
              {secondaryButtonLabel}
            </SecondaryButton>
          )}
        </ButtonBox>
        {!hideIntercom && (
          <Box maxWidth="482px" mt="32px">
            <SupportMessage
              data-testid="intercom"
              onClick={() => intercom.show()}
              type="info"
              description="Seen this error too many times? Get in touch with us on Live Chat"
            />
          </Box>
        )}

        {errorReference && (
          <Box mt="8px">
            <Text tag="p" typo="caption" align="center">
              Reference code: {errorReference}
            </Text>
          </Box>
        )}
      </ErrorPageContent>
    </PageWrapper>
  )
}

interface IButtonBox {
  $secondaryButtonLabel?: string
}

const ErrorPageContent = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 130px;
  align-items: center;
  transform: translateY(0);
  transition: all ease-in-out 0.6s;

  @media (min-width: 768px) {
    margin-top: 148px;
  }
`

const Description = styled.p`
  font-size: 14px;
  color: ${theme.colors.liquorice};
  line-height: 1.7;
  max-width: 657px;
  text-align: center;

  @media (min-width: 768px) {
    font-size: 16px;
  }
`

const ButtonBox = styled(Box)<IButtonBox>(
  ({ $secondaryButtonLabel }) => css`
    display: flex;
    align-items: center;
    flex-direction: column;
    justify-content: ${$secondaryButtonLabel ? 'space-between' : 'center'};
    margin-top: 24px;
    gap: 12px;

    ${$secondaryButtonLabel &&
    css`
      width: 100%;

      button {
        width: 100%;
      }
    `}

    @media (min-width: 768px) {
      margin-top: 48px;
      flex-direction: row;
      button {
        flex: 1 1 0;
        width: 100%;
      }
    }
  `,
)

const SecondaryButton = styled(Button)`
  margin-left: 10px;
`
